// Print (manuscript) letterforms, shapes and pre-writing paths for the universal
// `trace` engine. Same 0..100 metric as curriculumGeometry (cap top 16, x-height
// 46, baseline 78, descender 95) so TracingCanvas / LetterAnimator / scoring work
// unchanged. Multi-stroke glyphs use `M` per stroke (pen lifts).
import { GUIDES, STROKES, composeText, translatePath } from "./curriculumGeometry";

const PAD = 8, WORD_GAP = 20, MIN_WIDTH = 120;
const ADV_UP = 50, ADV_LOW = 40;

export const PRINT_UP = {
  A: "M 24 16 L 8 78 M 24 16 L 40 78 M 14 56 L 34 56",
  B: "M 10 16 L 10 78 M 10 16 L 28 16 C 40 16 40 46 28 46 L 10 46 M 10 46 L 30 46 C 42 46 42 78 30 78 L 10 78",
  C: "M 40 28 C 30 12 8 20 8 47 C 8 74 30 82 40 66",
  D: "M 10 16 L 10 78 M 10 16 L 22 16 C 44 16 44 78 22 78 L 10 78",
  E: "M 38 16 L 10 16 L 10 78 L 38 78 M 10 47 L 32 47",
  F: "M 38 16 L 10 16 L 10 78 M 10 47 L 32 47",
  G: "M 40 28 C 30 12 8 20 8 47 C 8 74 32 82 40 64 L 40 50 L 26 50",
  H: "M 10 16 L 10 78 M 40 16 L 40 78 M 10 47 L 40 47",
  I: "M 25 16 L 25 78 M 12 16 L 38 16 M 12 78 L 38 78",
  J: "M 34 16 L 34 66 C 34 82 12 82 12 66",
  K: "M 10 16 L 10 78 M 38 16 L 10 50 M 18 42 L 38 78",
  L: "M 10 16 L 10 78 L 38 78",
  M: "M 8 16 L 8 78 M 8 16 L 25 60 L 42 16 L 42 78",
  N: "M 10 16 L 10 78 M 10 16 L 40 78 L 40 16",
  O: "M 25 16 C 4 16 4 78 25 78 C 46 78 46 16 25 16",
  P: "M 10 16 L 10 78 M 10 16 L 28 16 C 42 16 42 48 28 48 L 10 48",
  Q: "M 25 16 C 4 16 4 78 25 78 C 46 78 46 16 25 16 M 30 62 L 42 80",
  R: "M 10 16 L 10 78 M 10 16 L 28 16 C 42 16 42 48 28 48 L 10 48 M 22 48 L 40 78",
  S: "M 38 26 C 30 12 8 16 10 32 C 12 46 38 44 40 62 C 42 80 14 84 8 68",
  T: "M 25 16 L 25 78 M 8 16 L 42 16",
  U: "M 10 16 L 10 62 C 10 84 40 84 40 62 L 40 16",
  V: "M 8 16 L 25 78 L 42 16",
  W: "M 6 16 L 16 78 L 25 36 L 34 78 L 44 16",
  X: "M 10 16 L 40 78 M 40 16 L 10 78",
  Y: "M 10 16 L 25 48 L 40 16 M 25 48 L 25 78",
  Z: "M 10 16 L 40 16 L 10 78 L 40 78",
};

export const PRINT_LOW = {
  a: "M 30 52 C 22 42 6 48 6 62 C 6 76 22 82 30 72 M 30 46 L 30 78",
  b: "M 8 16 L 8 78 M 8 56 C 16 42 34 48 34 62 C 34 76 16 82 8 68",
  c: "M 32 54 C 26 42 6 46 6 62 C 6 78 26 82 32 70",
  d: "M 30 56 C 22 42 6 48 6 62 C 6 76 22 82 30 68 M 30 16 L 30 78",
  e: "M 6 62 L 32 62 C 32 44 8 42 6 60 C 4 78 26 84 32 72",
  f: "M 28 20 C 18 14 14 22 14 32 L 14 78 M 6 46 L 24 46",
  g: "M 30 56 C 22 42 6 48 6 62 C 6 76 22 82 30 68 M 30 46 L 30 84 C 30 96 12 96 8 88",
  h: "M 8 16 L 8 78 M 8 58 C 14 44 32 44 32 60 L 32 78",
  i: "M 20 46 L 20 78 M 20 32 L 20 34",
  j: "M 24 46 L 24 84 C 24 96 8 96 6 88 M 24 32 L 24 34",
  k: "M 8 16 L 8 78 M 30 46 L 8 62 M 14 58 L 30 78",
  l: "M 20 16 L 20 78",
  m: "M 6 46 L 6 78 M 6 56 C 10 44 20 44 20 58 L 20 78 M 20 56 C 24 44 34 44 34 58 L 34 78",
  n: "M 8 46 L 8 78 M 8 58 C 14 44 32 44 32 60 L 32 78",
  o: "M 19 46 C 4 46 4 78 19 78 C 34 78 34 46 19 46",
  p: "M 8 46 L 8 95 M 8 56 C 16 42 34 48 34 62 C 34 76 16 82 8 68",
  q: "M 30 56 C 22 42 6 48 6 62 C 6 76 22 82 30 68 M 30 46 L 30 95",
  r: "M 8 46 L 8 78 M 8 58 C 12 46 24 44 30 50",
  s: "M 30 52 C 24 42 6 44 8 54 C 10 62 30 60 32 70 C 34 82 10 84 6 72",
  t: "M 16 20 L 16 70 C 16 80 26 80 30 74 M 6 46 L 28 46",
  u: "M 8 46 L 8 66 C 8 84 32 84 32 66 L 32 46 L 32 78",
  v: "M 6 46 L 19 78 L 32 46",
  w: "M 4 46 L 12 78 L 20 54 L 28 78 L 36 46",
  x: "M 6 46 L 32 78 M 32 46 L 6 78",
  y: "M 6 46 L 20 78 M 34 46 L 16 90 C 12 96 6 94 6 90",
  z: "M 6 46 L 32 46 L 6 78 L 32 78",
  ".": "M 10 75 L 10 78",
  ",": "M 10 76 L 8 84",
  "!": "M 10 16 L 10 62 M 10 74 L 10 78",
  "?": "M 4 26 C 8 12 26 14 24 30 C 22 40 12 40 12 56 M 12 74 L 12 78",
};

export const SHAPES = {
  circle: "M 50 20 C 10 20 10 80 50 80 C 90 80 90 20 50 20",
  square: "M 20 20 L 80 20 L 80 80 L 20 80 L 20 20",
  triangle: "M 50 18 L 84 80 L 16 80 L 50 18",
  rectangle: "M 12 30 L 88 30 L 88 70 L 12 70 L 12 30",
  oval: "M 50 26 C 12 26 12 74 50 74 C 88 74 88 26 50 26",
  diamond: "M 50 14 L 84 50 L 50 86 L 16 50 L 50 14",
};

// Extra pre-writing paths on top of the cursive STROKES set.
export const PATHS = {
  vertical: "M 50 18 L 50 82",
  horizontal: "M 15 50 L 85 50",
  verticals_repeat: "M 22 20 L 22 80 M 50 20 L 50 80 M 78 20 L 78 80",
  horizontals_repeat: "M 15 30 L 85 30 M 15 50 L 85 50 M 15 70 L 85 70",
  zigzag: "M 10 75 L 25 30 L 40 75 L 55 30 L 70 75 L 85 30",
  cross: "M 50 18 L 50 82 M 18 50 L 82 50",
  path_curvy: "M 8 60 C 22 20 40 90 56 50 C 66 25 78 70 92 40",
  path_straight: "M 8 50 L 92 50",
  path_hill: "M 8 78 C 30 20 70 20 92 78",
  big_curve: "M 10 70 C 25 15 75 15 90 70",
  little_curve: "M 30 62 C 40 40 60 40 70 62",
  smile: "M 15 40 C 30 80 70 80 85 40",
  rainbow: "M 10 80 C 20 20 80 20 90 80 M 25 80 C 32 40 68 40 75 80",
};

export const STROKE_IDS = [...Object.keys(PATHS), ...Object.keys(STROKES)];
export const SHAPE_IDS = Object.keys(SHAPES);

const firstMove = (d) => {
  const m = /M\s*(-?\d*\.?\d+)\s*[,\s]\s*(-?\d*\.?\d+)/.exec(d);
  return m ? { x: +m[1], y: +m[2] } : { x: 0, y: 0 };
};
const splitM = (d) => (d.match(/M[^M]*/g) || [d]).map((s) => s.trim());

function single(d) {
  return {
    viewBox: "0 0 100 100", width: 100,
    strokes: splitM(d).map((s) => ({ d: s, start: firstMove(s) })),
    startingPoint: firstMove(d), guidelines: GUIDES,
    get fullPath() { return this.strokes.map((x) => x.d).join(" "); },
  };
}

export function composePrint(text) {
  let strokes = [], cursor = PAD, start = null;
  for (const ch of text) {
    if (ch === " ") { cursor += WORD_GAP; continue; }
    const up = /[A-Z]/.test(ch);
    const punct = /[.,!?]/.test(ch);
    const d = up ? PRINT_UP[ch] : PRINT_LOW[punct ? ch : ch.toLowerCase()];
    if (!d) { cursor += ADV_LOW; continue; }
    for (const sub of splitM(d)) { const t = translatePath(sub, cursor, 0); strokes.push({ d: t, start: firstMove(t) }); }
    if (!start) start = strokes[strokes.length - splitM(d).length].start;
    cursor += up ? ADV_UP : punct ? 20 : ADV_LOW;
  }
  let width = cursor + PAD;
  if (width < MIN_WIDTH) {
    const dx = (MIN_WIDTH - width) / 2;
    strokes = strokes.map((s) => ({ d: translatePath(s.d, dx, 0), start: { x: s.start.x + dx, y: s.start.y } }));
    if (start) start = { x: start.x + dx, y: start.y };
    width = MIN_WIDTH;
  }
  return {
    viewBox: `0 0 ${width} 100`, width,
    strokes: strokes.length ? strokes : [{ d: `M ${PAD} 78 L ${width - PAD} 78` }],
    startingPoint: start || { x: PAD, y: 78 }, guidelines: GUIDES,
    get fullPath() { return this.strokes.map((x) => x.d).join(" "); },
  };
}

// Build a traceable from a `trace` activity config {kind, text, style}.
export function traceableFromConfig(cfg = {}) {
  const kind = cfg.kind || "letter";
  const text = String(cfg.text || "").trim();
  if (kind === "stroke") { const d = PATHS[text] || STROKES[text]?.d; return d ? single(d) : null; }
  if (kind === "shape") { const d = SHAPES[text]; return d ? single(d) : null; }
  if (!text) return null;
  return cfg.style === "cursive" ? composeText(text) : composePrint(text);
}
